import type { PaymentTile } from './order';

export type CardLocale = 'de' | 'ro' | 'en' | 'fr' | 'es' | 'pt' | 'zh' | 'hi' | 'ar' | 'bn' | 'it' | 'ru';

export enum CardAvailability {
  InStock = 'in_stock',
  OutOfStock = 'out_of_stock',
  Preorder = 'preorder',
  Backorder = 'backorder',
}

export interface CardI18nFields {
  name?: string;
  description?: string;
  h1?: string;
  metaTitle?: string;
  metaDescription?: string;
  terms?: string;
  redeemInstructions?: string;
}

export interface CardFaqItem {
  question: string;
  answer: string;
}

export interface CardI18nFaqItem {
  question?: string;
  answer?: string;
}

export interface CardI18nLocale {
  fields?: CardI18nFields;
  faq?: CardI18nFaqItem[];
}

export type CardI18n = Partial<Record<CardLocale, CardI18nLocale>>;

export interface CardFaceValue {
  min: number;
  max: number;
  /** A value must be min plus a multiple of it. */
  step?: number;
}

/**
 * One denomination of a card: a fixed value (min === max) or a range.
 */
export interface CardVariant {
  /** Unique inside one card. */
  sku: string;
  currency: string;
  countries?: string[];
  faceValue: CardFaceValue;
  availability: CardAvailability;
  /** Cards that can still be bought; null when the supplier does not count. */
  stock: number | null;
  /** Customer discount, percent. */
  discountPct: number;
}

export interface CardRobots {
  index?: boolean;
  follow?: boolean;
  /** index/follow above win over the locale-robots setting, in every locale. */
  overrideLocale?: boolean;
}

export interface CardSeo {
  h1?: string;
  metaTitle?: string;
  metaDescription?: string;
  robots?: CardRobots;
}

/**
 * A gift card as the API answers it to the storefront, in the names the
 * product is stored under.
 */
export interface Card {
  _id: string;
  /** The URL key: /giftcards/<slug>. */
  slug: string;
  name: string;
  description: string;
  terms: string;
  redeemInstructions: string;
  images?: { url: string }[];
  category?: string[];
  countries: string[];
  /** Out of stock also when the product is delisted or switched off. */
  availability: CardAvailability;
  /** The badge: the customer discount of the best variant, percent. */
  discountPct?: number;
  variants?: CardVariant[];
  /** Only signed-in customers may check out with this product. */
  requiresAuth?: boolean;
  /** Payment tiles the cart must not offer while it holds this product. */
  blockedPaymentTiles?: PaymentTile[];
  seo?: CardSeo;
  rating?: { score: number; count: number };
  faq?: CardFaqItem[];
  i18n?: CardI18n;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Paginated response for cards
 */
export interface PaginatedCards {
  items: Card[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Slim projection for card grids/sliders rendered by client components.
 * Server components MUST map full Card[] through toCardGridItem() before
 * passing them as client-component props: a full Card carries description,
 * terms, redeem instructions, FAQ and the 12-locale i18n block, all of which
 * would ride the RSC hydration payload (the brand page serialized ~620 KB of
 * it - SEO ticket ACEB-162). Client-side refetches may still hold full Cards;
 * Card is structurally assignable to CardGridItem.
 */
export type CardGridItem = Pick<
  Card,
  '_id' | 'slug' | 'name' | 'images' | 'countries' | 'discountPct' | 'availability' | 'rating' | 'i18n'
>;

// Accepts full Cards and already-slim items alike (idempotent), so callers
// holding CardGridItem-typed arrays that were filled with full Cards can map
// without casts.
export function toCardGridItem(card: CardGridItem, locale?: CardLocale): CardGridItem {
  const localizedName = locale ? card.i18n?.[locale]?.fields?.name : undefined;
  return {
    _id: card._id,
    slug: card.slug,
    name: card.name,
    images: card.images?.slice(0, 1),
    countries: card.countries,
    discountPct: getCardDiscount(card),
    availability: card.availability,
    rating: card.rating,
    // Only the active locale's name survives - the full i18n block is the
    // payload bomb. A locale switch is a fresh SSR render anyway.
    ...(localizedName !== undefined && {
      i18n: {
        [locale as CardLocale]: { fields: { name: localizedName } },
      } as CardI18n,
    }),
  };
}

/**
 * Card filtering options
 */
export interface CardFilters {
  limit?: number;
  offset?: number;
  country?: string;
  category?: string;
  search?: string;
  discount?: boolean;
  instock?: boolean;
}

/**
 * Pricing result for a card
 */
export interface PricingResult {
  price: number;
  price_no_discount: number;
  discount: number;
}

/**
 * Single card pricing query for multiple pricing request
 */
export interface CardPricingQuery {
  id: string;
  faceValue: number;
  currency: string;
  country?: string;
}

/**
 * Request DTO for getting pricing for multiple cards
 */
export interface GetCardsPricingDto {
  cards: CardPricingQuery[];
  couponCode?: string;
  desiredCurrency?: string;
}

/**
 * Price calculation result for a single card
 */
export interface PriceCalculationDto {
  productId: string;
  discount: number;
  price: number;
  priceNoDiscount: number;
}

/**
 * Response DTO for multiple cards pricing
 */
export interface CardsPricingDto {
  total: number;
  totalNoDiscount: number;
  currency: string;
  prices?: PriceCalculationDto[];
}

/**
 * A product slug with what the sitemap needs to know about it
 */
export interface BrandWithMetadata {
  /** The slug of the product. */
  name: string;
  updatedAt: string;
  noindex?: boolean;
  nofollow?: boolean;
  /** noindex/nofollow above win over the locale-robots setting for this brand. */
  overrideLocale?: boolean;
}

/**
 * Category with metadata
 */
export interface CategoryWithMetadata {
  name: string;
  updatedAt: string;
}

/** The picture of the card; empty when it has none. */
export function getCardImage(card: Pick<Card, 'images'> | null | undefined): string {
  return card?.images?.[0]?.url ?? '';
}

export function isVariantOutOfStock(variant: Pick<CardVariant, 'stock'>): boolean {
  return variant.stock === 0;
}

export function isCardOutOfStock(card: Pick<Card, 'availability'>): boolean {
  return card.availability !== CardAvailability.InStock;
}

export function isCardInStock(card: Pick<Card, 'availability'>): boolean {
  return !isCardOutOfStock(card);
}

/**
 * The card's display discount (positive %, 0 if none). The API has already
 * applied the caps and the minimum profit to it.
 */
export function getCardDiscount(card: Pick<Card, 'discountPct'>): number {
  return card.discountPct && card.discountPct > 0 ? card.discountPct : 0;
}

/** The variant a face value belongs to; the first one when several cover it. */
export function findCardVariant(
  card: Pick<Card, 'variants'>,
  faceValue: number,
  currency: string,
  country?: string
): CardVariant | undefined {
  return (card.variants ?? []).find(
    (variant) =>
      variant.faceValue.min <= faceValue &&
      variant.faceValue.max >= faceValue &&
      variant.currency === currency &&
      (!country || !variant.countries?.length || variant.countries.includes(country))
  );
}

export function isStockSufficient(
  card: Card,
  faceValue: number,
  currency: string,
  quantity: number,
  country?: string
): boolean {
  if (isCardOutOfStock(card)) {
    return false;
  }

  const variant = findCardVariant(card, faceValue, currency, country);
  if (!variant) {
    return false;
  }

  // The supplier does not count: no limit.
  if (variant.stock === null || variant.stock === undefined) {
    return true;
  }

  return variant.stock >= quantity;
}

function getI18nField(card: Pick<Card, 'i18n'> | undefined, locale: CardLocale | undefined, field: keyof CardI18nFields): string | undefined {
  if (!card || !locale || !card.i18n) return undefined;
  return card.i18n[locale]?.fields?.[field];
}

export function getCardTitle(card: Pick<Card, 'name' | 'i18n'> | undefined, locale?: CardLocale): string {
  if (!card) return '';
  return getI18nField(card, locale, 'name') || card.name;
}

export function getCardDescription(card: Card | undefined, locale?: CardLocale): string {
  if (!card) return '';
  return getI18nField(card, locale, 'description') || card.description;
}

export function getCardH1(card: Card | undefined, locale?: CardLocale): string | undefined {
  if (!card) return undefined;
  return getI18nField(card, locale, 'h1') || card.seo?.h1;
}

export function getCardTerms(card: Card | undefined, locale?: CardLocale): string {
  if (!card) return '';
  return getI18nField(card, locale, 'terms') || card.terms;
}

export function getCardRedeemInstructions(card: Card | undefined, locale?: CardLocale): string {
  if (!card) return '';
  return getI18nField(card, locale, 'redeemInstructions') || card.redeemInstructions;
}

export function getCardMetaTitle(card: Card | undefined, locale?: CardLocale): string | undefined {
  if (!card) return undefined;
  return getI18nField(card, locale, 'metaTitle') || card.seo?.metaTitle;
}

export function getCardMetaDescription(card: Card | undefined, locale?: CardLocale): string | undefined {
  if (!card) return undefined;
  return getI18nField(card, locale, 'metaDescription') || card.seo?.metaDescription;
}

export function getCardFaq(card: Card | undefined, locale?: CardLocale): CardFaqItem[] {
  if (!card) return [];
  const baseFaq = card.faq || [];
  if (!locale || !card.i18n?.[locale]?.faq?.length) return baseFaq;
  const localeFaq = card.i18n[locale].faq!;
  const maxLen = Math.max(baseFaq.length, localeFaq.length);
  const result: CardFaqItem[] = [];
  for (let i = 0; i < maxLen; i++) {
    const base = baseFaq[i];
    const loc = localeFaq[i];
    result.push({
      question: loc?.question || base?.question || '',
      answer: loc?.answer || base?.answer || '',
    });
  }
  return result;
}
